/**
 * The runtime half of a headless screenshot.
 *
 * WHY THIS EXISTS
 * ---------------
 * `screenshot` in a NullEngine host does not capture a frame — it exports the
 * live scene through `TraceScene._buildExportData()` and posts that JSON to a
 * renderer that rebuilds the scene and photographs it (src/render-bridge.ts).
 * That export is the AUTHORED document, and one thing it cannot carry is the
 * state a play session put the scene into:
 *
 *  - `transform` IS captured live, so a runtime pose rides along;
 *  - every behavior serialises its AUTHORED data (`TraceBehavior.serialize()`
 *    returns `structuredClone(this._authored)`), so a Properties declaration
 *    goes over the wire at its declared `value`, never its current one.
 *
 * Rebuilding that on the render page produces a picture that is part runtime
 * and part authored, and therefore a state the scene was never in: a bound
 * transform is re-derived from the authored property value and snaps back to
 * the rest pose, while an unbound one keeps the pose the runtime gave it. The
 * balance-scale arm of loop 4 spent three turns on the result — a tilted beam
 * over weights sitting in their tray with every readout at zero, taken
 * moments after `probe_runtime` reported all four weights on the left pan.
 *
 * WHAT THIS DOES
 * --------------
 * Carry the current value of every declared property alongside the scene and
 * write it back AFTER the render page has loaded and fired its start events.
 * After is the load-bearing word: a start graph routinely resets the very
 * properties a play session moved (the balance's own `onStart` sets
 * `location1..4 = 0`, `leftMass = 0`, `trayMass = 10`), so a value baked into
 * the declaration would be overwritten a moment after it was read. Applied
 * last, it wins — and every binding downstream of it re-derives, which is what
 * puts the beam, the weights and the readouts back into agreement.
 *
 * Properties are the whole payload because in Trace they are the whole runtime
 * model: a scene's moving parts are bound to them. What they cannot carry —
 * Timeline position, BehaviorGraph variables, script state, a transition
 * caught mid-flight — stays outside the picture, same as before.
 */

import { Properties, type PropertyValue } from 'trace-viewer/behaviors';
import type { TraceScene } from 'trace-viewer/scene';

/** One object's live property values, keyed by the path the export writes. */
export interface RuntimeStateEntry {
    /**
     * Full scene path (`Scene/Rig/Beam`). Matched against the reloaded scene,
     * falling back to the trailing segment exactly like the parent lookup in
     * `e2e/load-bench-scene.ts` — a flat scene resolves by bare name.
     */
    path: string;
    /** Every declared property and the value it currently holds. */
    properties: Record<string, PropertyValue>;
}

/** The little we need from a rebuilt scene, so this stays importable anywhere. */
export interface RuntimeStateTarget {
    resolveSceneObject(path: string): { getBehavior(type: typeof Properties): Properties | null } | null;
}

/**
 * Read every Properties behavior's current values.
 *
 * Declared names rather than overridden ones: `getPropertyNames()` covers
 * sheet-declared properties too, and a value that happens to equal its
 * declaration costs nothing to re-apply. Sampling is a snapshot by design —
 * a driven property freezes at the instant the picture was asked for.
 */
export function collectRuntimeState(traceScene: TraceScene): RuntimeStateEntry[] {
    const entries: RuntimeStateEntry[] = [];
    for (const object of traceScene.getSceneObjects()) {
        const properties = object.getBehavior(Properties);
        if (!properties) continue;
        const values: Record<string, PropertyValue> = {};
        let any = false;
        for (const name of properties.getPropertyNames()) {
            const value = properties.getValue(name);
            if (value === undefined) continue;
            values[name] = value;
            any = true;
        }
        if (any) entries.push({ path: object.getPath(), properties: structuredClone(values) });
    }
    return entries;
}

/**
 * Write those values into a freshly loaded scene. Returns the number of
 * objects that took at least one.
 *
 * Failures are silent on purpose: this runs inside a capture, and a renamed or
 * dropped object must cost the picture nothing more than that object's runtime
 * state. The scene was already going to be drawn.
 */
export function applyRuntimeState(target: RuntimeStateTarget, entries: RuntimeStateEntry[] | undefined): number {
    if (!entries?.length) return 0;
    let applied = 0;
    for (const entry of entries) {
        const object = target.resolveSceneObject(entry.path)
            ?? target.resolveSceneObject(entry.path.split('/').pop() ?? '');
        const properties = object?.getBehavior(Properties);
        if (!properties) continue;
        let wrote = false;
        for (const [name, value] of Object.entries(entry.properties)) {
            // Snap. A declaration carrying its own transition would otherwise
            // animate toward the sampled value, and the capture settles on a
            // frame count — the picture would catch the fade, not the state.
            if (properties.setValue(name, value, { duration: 0 })) wrote = true;
        }
        if (wrote) applied++;
    }
    return applied;
}
